/**
 * @description Displays a report of all the failed personnel syncs by row number and reason
 */

import React from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

import NoResults from '@seccon/shared/ui/components/NoResults';

import { SyncResultsContext } from '../contexts/SyncResults';

const styles = {
  errorMessage: {
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    marginBottom: 1,

    '&:last-of-type': {
      marginBottom: 0
    }
  },
  heading: {
    width: '100%'
  },
  innerTableContainer: {
    width: '50%',
    minWidth: '900px'
  },
  outerTableContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    width: '100%'
  },
  rowNumeric: {
    textAlign: 'center'
  },
  subheading: {
    color: 'text.secondary'
  },
  table: {
    width: '100%'
  },
  tableColumnRowNumber: {
    textAlign: 'center',
    textWrap: 'nowrap',
    width: '100px'
  }
}

const FailedPartialSyncs = () => {
  const results = React.useContext(SyncResultsContext);
  const { personnelErrors: list = {} } = results ?? {};

  // Keeps track of the number of rows per page to display
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  // Keeps track of the current page in the table
  const [page, setPage] = React.useState(0);

  /**
   * Handles page changes by setting the new page value
   * @param newValue {number} - new current page value
   */
  function handlePageChange (newValue) {
    setPage(newValue);
  }

  /**
   * Handles changes in the row per page setting by saving the state and resetting the page back to the first page
   * @param newValue {number} - new rows per page setting
   */
  function handleChangeRowsPerPage (newValue) {
    setRowsPerPage(newValue);
    setPage(0);
  }

  // Calculate the total number of records and the current page's data set
  const [pageData, total] = React.useMemo(() => {
    const keyList = Object.keys(list);

    return [
      keyList.slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage),
      keyList.length
    ];
  }, [list, page, rowsPerPage]);

  return (
    <>
      <Box>
        <Typography sx={styles.heading} variant='h6'>Failed / Partial Syncs</Typography>
        <Typography sx={styles.subheading} variant='subtitle2'>Records that failed to sync or synced partially</Typography>
      </Box>
      {total > 0 ? (
        <Box sx={styles.outerTableContainer}>
          <Paper sx={styles.innerTableContainer}>
            <TableContainer>
              <Table sx={styles.table}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.tableColumnRowNumber}>Row Number</TableCell>
                    <TableCell>Error Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageData.map((key) => (
                    <TableRow key={`row${ key }`} sx={styles.tableBodyRow}>
                      <TableCell sx={styles.rowNumeric}>{key}</TableCell>
                      <TableCell>
                        {list[key].map((entry, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Typography key={index} sx={styles.errorMessage}>{entry}</Typography>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component='div'
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, value) => handlePageChange(value)}
              onRowsPerPageChange={(evt) => handleChangeRowsPerPage(parseInt(evt.target.value, 10))}
            />
          </Paper>
        </Box>
      ) : (
        <NoResults Icon={CheckCircleOutlineOutlinedIcon}>No Failed / Partial Syncs</NoResults>
      )}
    </>
  );
};

export default FailedPartialSyncs;
